import { cloneDeep, isNumber } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { mapRange } from '@/utils/math-kit';
import {
    LayerType,
    MaterialIdsType,
    TextShapeType,
    type ILayerProps,
    type IShapeExtension,
    type ITextLayerProps,
} from '@/version-types';
import { defaultShapeParams, getShapeTypeConfig } from '../config';
import { Events } from '../constants';
import type { ShapeTextRenderer } from '../edgeless/shape-text-renderer';
import { type ShapeTextProps } from '../types';
import {
    applySnapshotSize,
    collectFontSubsetRequests,
    pushHistory,
    refreshRuntimeFontFamilies,
    updateLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';

export type UnsetOptions = {
    update?: boolean;
    removeMaterialID?: boolean;
};

export type ShapeTextPropsOptions = {
    props: ShapeTextProps;
    writeHistory?: boolean;
};

export interface IShapeText {
    /**
     * 应用变形文字
     *
     * @param layerID - 图层 ID
     * @param shapeType - 形状类型
     * @param options - 素材 ID 与已有变形参数
     * @returns 已提交的 Shape Text 图层；目标无效或类型未变化时返回 undefined
     */
    applyShapeText(
        layerID: string,
        shapeType: TextShapeType,
        options?: { materialID?: number; params?: IShapeExtension },
    ): Promise<ILayerProps | undefined>;

    /**
     * 移除变形文字效果
     *
     * @param layerID - 图层 ID
     * @param options - 是否提交舞台以及是否移除素材 ID
     * @returns 恢复后的普通 Text Layer；目标或排版不可用时返回 undefined
     */
    cancelShapeEffect(layerID: string, options?: UnsetOptions): Promise<ILayerProps | undefined>;

    /**
     * 调节变形文字参数
     *
     * @param layerID - 图层 ID
     * @param key - 参数名
     * @param value - 面板值
     * @param options - 参数映射配置与是否写历史
     * @returns 当前仍可发布的图层；过期 live 任务返回 undefined
     */
    adjustShapeTextProps(
        layerID: string,
        key: string,
        value: number | boolean,
        options: ShapeTextPropsOptions,
    ): Promise<ILayerProps | undefined>;

    /**
     * 根据候选图层生成无正式副作用的稳定 Shape Text 投影
     *
     * @param layer - Shape Text 候选图层
     * @returns 带最新几何与 screenshotUrl 的稳定图层副本
     */
    refreshShapeTextRender(layer: ILayerProps): Promise<ILayerProps>;

    /**
     * 预加载变形文字资源
     *
     * @returns 资源全部就绪后的 Promise
     */
    loadShapeTextAssets(): Promise<void>;
}

const FUNC_ID = trackConfig[LayerType.TEXT_LAYER].transformEffect;

export class ShapeText implements IShapeText {
    private static readonly shapeTypeConfig = getShapeTypeConfig();
    private adjustmentBaseline: ILayerProps | null = null;
    private adjustmentOwnsLive = false;

    constructor(
        private readonly framework: IFramework,
        private readonly renderer: ShapeTextRenderer,
    ) {}

    loadShapeTextAssets() {
        return this.renderer.preload();
    }

    async applyShapeText(
        layerID: string,
        shapeType: TextShapeType,
        options: { materialID?: number; params?: IShapeExtension } = {},
    ) {
        this.restoreOtherAdjustment(layerID);
        const current = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!current || current.layerType !== LayerType.TEXT_LAYER) return;
        const currentShape = (current.data as ITextLayerProps).shapeExtension;
        if (currentShape?.curveTextType === shapeType) return;

        const baseline = this.takeBaseline(current);
        const candidate = this.createShapeTextLayer(current, shapeType, options);
        if (!candidate) return;
        const ownsLive = !this.renderer.isLive(layerID);

        this.framework.service.loading.add(layerID);
        try {
            const liveLayer = await this.renderer.render(candidate, 'live');
            if (!liveLayer) return;
            const liveDrawableSettled = this.waitForDrawableSettled(layerID);
            updateLayer(this.framework, liveLayer);
            await liveDrawableSettled;

            const nextLayer = await this.renderer.render(liveLayer, 'stable');
            if (!nextLayer) return;
            this.renderer.settleLive(nextLayer);
            if (ownsLive) await this.renderer.finishGLRender(nextLayer.id);
            const stableDrawableSettled = this.waitForDrawableSettled(layerID);
            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, baseline, nextLayer, FUNC_ID);
            await stableDrawableSettled;
            return nextLayer;
        } catch (error) {
            this.restoreStableLayer(layerID, !ownsLive);
            throw error;
        } finally {
            this.adjustmentBaseline = null;
            this.adjustmentOwnsLive = false;
            this.framework.service.loading.remove(layerID);
        }
    }

    async cancelShapeEffect(layerID: string, options: UnsetOptions = {}) {
        this.restoreOtherAdjustment(layerID);
        const current = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!current || current.layerType !== LayerType.TEXT_LAYER) return;
        if (!(current.data as ITextLayerProps).shapeExtension) return;

        const baseline = this.takeBaseline(current);
        const keepLive = this.renderer.isLive(layerID) && !this.adjustmentOwnsLive;
        const stable = this.renderer.reset(layerID, { keepLive });

        const { update = true, removeMaterialID = true } = options;
        const nextLayer = await this.resolveCancelledLayer(stable || current, removeMaterialID);
        if (!nextLayer) return;

        this.adjustmentBaseline = null;
        this.adjustmentOwnsLive = false;
        if (update) {
            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, baseline, nextLayer, FUNC_ID);
        }
        return nextLayer;
    }

    async adjustShapeTextProps(layerID: string, key: string, value: number | boolean, options: ShapeTextPropsOptions) {
        this.restoreOtherAdjustment(layerID);
        const current = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!current || current.layerType !== LayerType.TEXT_LAYER) return;
        const data = current.data as ITextLayerProps;
        if (!data.shapeExtension) return;

        const { props, writeHistory = true } = options;
        if (!this.adjustmentBaseline || this.adjustmentBaseline.id !== layerID) {
            this.adjustmentBaseline = cloneDeep(current);
            this.adjustmentOwnsLive = !this.renderer.isLive(layerID);
        }

        let nextValue = value;
        if (isNumber(value) && 'range' in props) nextValue = mapRange(value, props.range, props.ratio);

        const candidate: ILayerProps = {
            ...cloneDeep(current),
            data: {
                ...cloneDeep(data),
                shapeExtension: { ...data.shapeExtension, [key]: nextValue },
            },
        };

        try {
            let nextLayer: ILayerProps | null;
            if (writeHistory) {
                const liveLayer = await this.renderer.render(candidate, 'live');
                if (!liveLayer) return;
                nextLayer = await this.renderer.render(liveLayer, 'stable');
                if (nextLayer) this.renderer.settleLive(nextLayer);
            } else {
                nextLayer = await this.renderer.render(candidate, 'live');
            }
            if (!nextLayer) return;
            updateLayer(this.framework, nextLayer);

            if (writeHistory) {
                if (this.adjustmentOwnsLive) await this.renderer.finishGLRender(nextLayer.id);
                pushHistory(this.framework, this.adjustmentBaseline, nextLayer, FUNC_ID);
                this.adjustmentBaseline = null;
                this.adjustmentOwnsLive = false;
            }
            return nextLayer;
        } catch (error) {
            this.restoreStableLayer(layerID, !this.adjustmentOwnsLive);
            this.adjustmentBaseline = null;
            this.adjustmentOwnsLive = false;
            throw error;
        }
    }

    async refreshShapeTextRender(layer: ILayerProps) {
        const stableLayer = await this.renderer.render(layer, 'stable');
        if (!stableLayer) throw new Error(`Shape text stable render was superseded: ${layer.id}`);
        return stableLayer;
    }

    private takeBaseline(current: ILayerProps) {
        return this.adjustmentBaseline?.id === current.id ? cloneDeep(this.adjustmentBaseline) : cloneDeep(current);
    }

    private restoreStableLayer(layerID: string, keepLive = false) {
        const stable = this.renderer.reset(layerID, { keepLive });
        if (stable) updateLayer(this.framework, stable, { activate: false });
    }

    private restoreOtherAdjustment(layerID: string) {
        if (!this.adjustmentBaseline || this.adjustmentBaseline.id === layerID) return;
        this.restoreStableLayer(this.adjustmentBaseline.id, !this.adjustmentOwnsLive);
        this.adjustmentBaseline = null;
        this.adjustmentOwnsLive = false;
    }

    private waitForDrawableSettled(layerID: string): Promise<boolean> {
        if (!this.framework.service.edgelessCanvas.getByLayerId(layerID)) return Promise.resolve(true);

        return new Promise((resolve) => {
            const handler = this.framework.service.events.on(
                Events.DrawableSettled,
                (settledLayerID: string, ready: boolean) => {
                    if (settledLayerID !== layerID) return;
                    this.framework.service.events.off(handler);
                    resolve(ready);
                },
            );
        });
    }

    private createShapeTextLayer(
        prevLayer: ILayerProps,
        shapeType: TextShapeType,
        options: { materialID?: number; params?: IShapeExtension } = {},
    ) {
        const config = ShapeText.shapeTypeConfig[shapeType];
        if (!config) return;

        const shapeExtension: IShapeExtension = options.params
            ? cloneDeep(options.params)
            : { curveTextType: shapeType, ...defaultShapeParams };

        if (!options.params) {
            config.forEach((item) => {
                shapeExtension[item.param] =
                    typeof item.value === 'boolean' ? item.value : mapRange(item.value, item.range, item.ratio);
            });
        }

        const materialIds = cloneDeep(prevLayer.materialIds || {});
        if (options.materialID) materialIds[MaterialIdsType.TEXT_TRANSFORM] = [`${options.materialID}`];

        return {
            ...cloneDeep(prevLayer),
            data: { ...cloneDeep(prevLayer.data as ITextLayerProps), shapeExtension },
            materialIds,
        } as ILayerProps;
    }

    private async resolveCancelledLayer(prevLayer: ILayerProps, removeMaterialID: boolean) {
        let nextLayer: ILayerProps = {
            ...cloneDeep(prevLayer),
            data: {
                ...cloneDeep(prevLayer.data as ITextLayerProps),
                shapeExtension: null,
                screenshotUrl: null,
            },
        };

        if (removeMaterialID) {
            nextLayer.materialIds = {
                ...nextLayer.materialIds,
                [MaterialIdsType.TEXT_TRANSFORM]: [],
            };
        }

        const center = {
            x: nextLayer.left + nextLayer.width / 2,
            y: nextLayer.top + nextLayer.height / 2,
        };
        nextLayer = refreshRuntimeFontFamilies(nextLayer);

        const fontSubsetResults = await Promise.all(
            collectFontSubsetRequests(nextLayer).map(async ({ fontFamily, chars }) => {
                if (!this.framework.service.font.getByFamily(fontFamily)) return false;
                const subsetFamily = await this.framework.service.font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return false;
                setLayerRuntimeValue(nextLayer, {
                    fontFamilyMap: {
                        ...(nextLayer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return true;
            }),
        );
        if (!fontSubsetResults.every(Boolean)) return;

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        if (resolution?.status !== 'ready') return;
        nextLayer = applySnapshotSize(nextLayer, resolution.value);
        return {
            ...nextLayer,
            left: Math.floor(center.x - nextLayer.width / 2),
            top: Math.floor(center.y - nextLayer.height / 2),
        };
    }
}
